import { GetStaticPropsContext } from "next";
import Head from "next/head";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useState } from "react";

import NeedHelp from "@shared/components/src/NeedHelp";
import { Alert, Button, Input, PicterLogo, Text } from "@shared/elements";

import DefaultOpenGraphTags from "src/components/DefaultOpenGraphTags";

import * as constants from "src/constants";
import bundleMessages from "src/utils/bundleMessages";

/**
 * Password Recovery Page: users can request a password recovery email here.
 * The email will contain a link to the password reset page.
 */

export default function PasswordRecoveryPage() {
  const t = useTranslations(
    `${constants.MESSAGE_PREFIX}pages.password-recovery`,
  );
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/password-resets`,
      {
        credentials: "include",
        method: "POST",
        body: JSON.stringify({ email }),
      },
    );

    if (res.status === 404) {
      setError(t("errorUnknownEmail"));
    } else if (res.status === 403) {
      setError(t("tooManyRequests"));
    } else if (res.status >= 300) {
      setError(t("anyError"));
    } else {
      setIsSuccess(true);
    }
    setIsSubmitting(false);
  };

  return (
    <>
      <Head>
        <title key="title">Picter – {t("title")}</title>
        <DefaultOpenGraphTags />
      </Head>
      <div className="min-h-full flex items-center justify-center">
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div>
              <Link href={constants.paths.index} aria-label="Picter">
                <PicterLogo />
              </Link>
              <Text as="h2" size="3xl" className="mt-8" weight="bold">
                {t("title")}
              </Text>
              <Text className="mt-2" color="muted" size="sm">
                {t("description")}
              </Text>
            </div>

            <div className="mt-8">
              <div className="mt-6">
                {isSuccess ? (
                  <Alert color="primary">{t("successMessage")}</Alert>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        label={t("emailLabel")}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                    {error && <Alert color="red">{error}</Alert>}
                    <div>
                      <Button
                        variant="primary"
                        type="submit"
                        className="w-full"
                        inProgress={isSubmitting}
                      >
                        {t("submitButton")}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
              <NeedHelp className="mt-6" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  const messages = await bundleMessages(locale, [
    `${constants.MESSAGE_PREFIX}pages.password-recovery`,
    ...NeedHelp.messages,
  ]);

  return {
    props: {
      messages,
    },
  };
}
